import type { Permission, SessionBootstrap } from '@blesk/contracts';
import { hasPermission } from './session';

/**
 * §13, §14 ТЗ. Двенадцать разделов админки и дашборд — одной таблицей.
 *
 * Таблица одна на всё: по ней строится меню, по ней же каждый экран проверяет
 * право на вход по прямой ссылке. Пункт, невидимый в меню, но открывающийся
 * по адресу, — типовая дыра; здесь она невозможна по построению.
 *
 * Скрытие пункта — удобство, а не контроль доступа: сервер проверяет право
 * заново в каждом хендлере (§3).
 */
export type SectionPath =
  | '/'
  | '/projects'
  | '/people'
  | '/invites'
  | '/survey-templates'
  | '/schedules'
  | '/negative-rules'
  | '/results'
  | '/incidents'
  | '/requests'
  | '/employees'
  | '/integration'
  | '/audit';

export type NavGroup = 'Обзор' | 'Справочники' | 'Опросы' | 'События' | 'Администрирование';

export interface Section {
  readonly path: SectionPath;
  readonly title: string;
  readonly group: NavGroup;
  readonly permission: Permission;
  /** Пункт ТЗ, которым раздел обоснован. */
  readonly spec: string;
}

export const SECTIONS: readonly Section[] = [
  { path: '/', title: 'Дашборд', group: 'Обзор', permission: 'dashboard.read', spec: '§14' },

  {
    path: '/projects',
    title: 'Объекты',
    group: 'Справочники',
    permission: 'projects.read',
    spec: '§13',
  },
  {
    path: '/people',
    title: 'Администраторы заказчика',
    group: 'Справочники',
    permission: 'people.read',
    spec: '§13',
  },
  {
    path: '/invites',
    title: 'Приглашения',
    group: 'Справочники',
    permission: 'invites.read',
    spec: '§13',
  },

  {
    path: '/results',
    title: 'Результаты опросов',
    group: 'Опросы',
    permission: 'results.read',
    spec: '§13',
  },
  {
    path: '/survey-templates',
    title: 'Шаблоны и версии опросов',
    group: 'Опросы',
    permission: 'surveys.read',
    spec: '§13',
  },
  {
    path: '/schedules',
    title: 'Расписания и напоминания',
    group: 'Опросы',
    permission: 'surveys.read',
    spec: '§13',
  },
  {
    path: '/negative-rules',
    title: 'Правила негативной оценки',
    group: 'Опросы',
    permission: 'surveys.read',
    spec: '§13',
  },

  {
    path: '/incidents',
    title: 'Инциденты',
    group: 'События',
    permission: 'results.read',
    spec: '§13',
  },
  {
    path: '/requests',
    title: 'Обращения',
    group: 'События',
    permission: 'results.read',
    spec: '§13',
  },

  {
    path: '/employees',
    title: 'Пользователи и роли',
    group: 'Администрирование',
    permission: 'employees.read',
    spec: '§13',
  },
  {
    path: '/integration',
    title: 'Интеграции и журнал ошибок',
    group: 'Администрирование',
    permission: 'integration.read',
    spec: '§13',
  },
  {
    path: '/audit',
    title: 'Аудит действий',
    group: 'Администрирование',
    permission: 'audit.read',
    spec: '§13',
  },
] as const;

/**
 * Порядок групп — по частоте обращения. Справочники настраивают один раз,
 * а инциденты и обращения разбирают каждый день, поэтому «События» стоят
 * выше: меню читается сверху, и первым должно попадаться то, за чем пришли.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  'Обзор',
  'События',
  'Опросы',
  'Справочники',
  'Администрирование',
];

export function sectionByPath(path: string): Section | undefined {
  return SECTIONS.find((section) => section.path === path);
}

/** Разделы, доступные роли. Порядок сохраняется — меню не должно прыгать. */
export function visibleSections(session: SessionBootstrap): Section[] {
  return SECTIONS.filter((section) => hasPermission(session, section.permission));
}

/** Группы, в которых роли доступен хотя бы один раздел: пустых заголовков нет. */
export function visibleGroups(session: SessionBootstrap): Array<{
  group: NavGroup;
  sections: Section[];
}> {
  return NAV_GROUPS.map((group) => ({
    group,
    sections: visibleSections(session).filter((section) => section.group === group),
  })).filter((entry) => entry.sections.length > 0);
}
